import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface CareerPosting {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  description: string;
  requirements: string[];
  benefits: string[];
  salary?: string;
  linkedInUrl?: string;
  closingDate?: string;
}

interface ApplicationForm {
  fullName: string;
  email: string;
  phone: string;
  linkedInUrl: string;
  coverLetter: string;
}

const CareersPage = () => {
  const [postings, setPostings] = useState<CareerPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosting, setSelectedPosting] = useState<CareerPosting | null>(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<ApplicationForm>({
    fullName: '', email: '', phone: '', linkedInUrl: '', coverLetter: '',
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [, setCoverLetterFile] = useState<File | null>(null);

  // Filters
  const [activeDepartment, setActiveDepartment] = useState('All');
  const [activeType, setActiveType] = useState('All');
  const [activeLocation, setActiveLocation] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchPostings = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/careers`);
        setPostings(data?.data || []);
      } catch {
        setPostings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPostings();
  }, []);

  // Extract unique filter values from postings
  const departments = useMemo(() => {
    return [...new Set(postings.map(p => p.department))].sort();
  }, [postings]);

  const types = useMemo(() => {
    return [...new Set(postings.map(p => p.type))].sort();
  }, [postings]);

  const locations = useMemo(() => {
    return [...new Set(postings.map(p => p.location))].sort();
  }, [postings]);

  // Apply filters
  const filteredPostings = useMemo(() => {
    let result = postings;

    if (activeDepartment !== 'All') {
      result = result.filter(p => p.department === activeDepartment);
    }

    if (activeType !== 'All') {
      result = result.filter(p => p.type === activeType);
    }

    if (activeLocation !== 'All') {
      result = result.filter(p => p.location === activeLocation);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(p => p.title.toLowerCase().includes(query));
    }

    return result;
  }, [postings, activeDepartment, activeType, activeLocation, searchQuery]);

  const hasActiveFilters = activeDepartment !== 'All' || activeType !== 'All' || activeLocation !== 'All' || searchQuery.trim() !== '';

  const clearFilters = () => {
    setActiveDepartment('All');
    setActiveType('All');
    setActiveLocation('All');
    setSearchQuery('');
  };

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPosting) return;
    if (!cvFile) { alert('Please upload your CV/Resume.'); return; }
    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      if (formData.phone) submitData.append('phone', formData.phone);
      if (formData.linkedInUrl) submitData.append('linkedInUrl', formData.linkedInUrl);
      if (formData.coverLetter) submitData.append('coverLetter', formData.coverLetter);
      submitData.append('resume', cvFile);

      await axios.post(`${API_BASE}/careers/${selectedPosting.id}/apply`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
      setFormData({ fullName: '', email: '', phone: '', linkedInUrl: '', coverLetter: '' });
      setCvFile(null);
      setCoverLetterFile(null);
    } catch {
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelectedPosting(null);
    setShowApplicationForm(false);
    setSubmitted(false);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-aos="slide-right">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-100" data-aos="slide-left" data-aos-delay="100">
              Build the future of cloud technology with us. We are looking for talented people who share our passion for innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Postings Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Filters */}
          {postings.length > 0 && (
            <div className="mb-8 space-y-4" data-aos="fade-up">
              {/* Search */}
              <div className="max-w-md mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search positions by title..."
                    className="w-full px-4 py-2.5 pl-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                  />
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Department Tabs */}
              {departments.length > 1 && (
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    onClick={() => setActiveDepartment('All')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeDepartment === 'All'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All Departments
                  </button>
                  {departments.map(dept => (
                    <button
                      key={dept}
                      onClick={() => setActiveDepartment(dept)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeDepartment === dept
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {dept}
                    </button>
                  ))}
                </div>
              )}

              {/* Type and Location Filters */}
              <div className="flex flex-wrap gap-3 justify-center">
                {types.length > 1 && (
                  <select
                    value={activeType}
                    onChange={(e) => setActiveType(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="All">All Types</option>
                    {types.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                )}

                {locations.length > 1 && (
                  <select
                    value={activeLocation}
                    onChange={(e) => setActiveLocation(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  >
                    <option value="All">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                )}

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 text-sm text-primary hover:text-primary-800 font-medium transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-48 animate-pulse" />
              ))}
            </div>
          ) : filteredPostings.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-50 rounded-xl flex items-center justify-center">
                <i className="fas fa-briefcase text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
                {hasActiveFilters ? 'No Matching Positions' : 'No Open Positions'}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {hasActiveFilters
                  ? 'Try adjusting your search or filter criteria.'
                  : "We don't have any open positions right now, but check back soon. We are always growing."}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 text-sm font-medium text-primary hover:text-primary-800 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPostings.map((posting) => (
                <div
                  key={posting.id}
                  onClick={() => setSelectedPosting(posting)}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group"
                  data-aos="fade-up"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-heading font-bold text-gray-900 group-hover:text-primary transition-colors">
                      {posting.title}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-primary-50 text-primary-600 rounded-full flex-shrink-0 ml-3">
                      {posting.department}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                      <i className="fas fa-map-marker-alt text-gray-400 text-xs"></i>
                      {posting.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                      <i className="fas fa-clock text-gray-400 text-xs"></i>
                      {posting.type}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                      <i className="fas fa-chart-line text-gray-400 text-xs"></i>
                      {posting.experience}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{posting.description}</p>
                  {posting.salary && (
                    <p className="text-sm font-medium text-primary mt-3">{posting.salary}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail / Application Modal */}
      {selectedPosting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-lg font-heading font-bold text-gray-900">{selectedPosting.title}</h2>
                <p className="text-sm text-gray-500">{selectedPosting.department} | {selectedPosting.location} | {selectedPosting.type}</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              {!showApplicationForm && !submitted ? (
                <>
                  <p className="text-gray-700 mb-6 whitespace-pre-line">{selectedPosting.description}</p>

                  {selectedPosting.requirements.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Requirements</h4>
                      <ul className="space-y-1.5">
                        {selectedPosting.requirements.map((req, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedPosting.benefits.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Benefits</h4>
                      <ul className="space-y-1.5">
                        {selectedPosting.benefits.map((ben, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            {ben}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedPosting.salary && (
                    <p className="text-sm text-gray-700 mb-4"><span className="font-medium">Salary:</span> {selectedPosting.salary}</p>
                  )}

                  {selectedPosting.closingDate && (
                    <p className="text-sm text-gray-500 mb-6">Applications close: {new Date(selectedPosting.closingDate).toLocaleDateString()}</p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowApplicationForm(true)}
                      className="px-6 py-2.5 bg-primary hover:bg-primary-800 text-white rounded-lg font-semibold transition-colors"
                    >
                      Apply Now
                    </button>
                    {selectedPosting.linkedInUrl && (
                      <a
                        href={selectedPosting.linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors inline-flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                        Apply via LinkedIn
                      </a>
                    )}
                  </div>
                </>
              ) : submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
                    <i className="fas fa-check text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-gray-900 mb-2">Application Submitted</h3>
                  <p className="text-gray-600">Thank you for your interest. We will review your application and get back to you soon.</p>
                  <button onClick={closeModal} className="mt-6 px-6 py-2.5 bg-primary hover:bg-primary-800 text-white rounded-lg font-semibold transition-colors">
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApply} className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Apply for {selectedPosting.title}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input type="text" value={formData.fullName} onChange={e => setFormData({ ...formData, fullName: e.target.value })} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input type="tel" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                      <input type="url" value={formData.linkedInUrl} onChange={e => setFormData({ ...formData, linkedInUrl: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="https://linkedin.com/in/..." />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload CV / Resume *</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      required
                      onChange={e => setCvFile(e.target.files?.[0] || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 transition-all"
                    />
                    <p className="text-xs text-gray-400 mt-1">PDF, DOC, or DOCX (max 10MB)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter (optional)</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={e => setCoverLetterFile(e.target.files?.[0] || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message to Hiring Manager</label>
                    <textarea value={formData.coverLetter} onChange={e => setFormData({ ...formData, coverLetter: e.target.value })} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="Briefly tell us why you are interested in this role..." />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-2.5 bg-primary hover:bg-primary-800 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowApplicationForm(false)}
                      className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersPage;
